import React, { useEffect, useState } from "react";
import { Box, Chip } from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { fetchPatientAppointments, isAbortError } from "./api";
import { PR_COLORS } from "./constants";
import { formatLongDate } from "./formatters";
import SectionDataGrid from "./SectionDataGrid";

/** A cancelled booking must never read as a visit that happened. */
const STATUS_COLOR = {
  COMPLETED: "success",
  CANCELLED: "error",
  SCHEDULED: "info",
};

/**
 * Sorting runs on the raw field and formatting happens in the cell, so the Date
 * column orders chronologically rather than alphabetically by "12 Jul 2026".
 */
const COLUMNS = [
  {
    field: "startTime",
    headerName: "Date",
    width: 130,
    renderCell: (params) => (
      <Box
        component="span"
        sx={{ fontWeight: 600, color: PR_COLORS.primary, whiteSpace: "nowrap" }}
      >
        {formatLongDate(params.value)}
      </Box>
    ),
  },
  {
    field: "doctorName",
    headerName: "Doctor",
    flex: 1,
    minWidth: 160,
    valueGetter: ({ value }) => value || "Doctor not recorded",
  },
  {
    field: "status",
    headerName: "Status",
    width: 130,
    renderCell: (params) =>
      params.value ? (
        <Chip
          size="small"
          label={params.value}
          color={STATUS_COLOR[params.value.toUpperCase()] || "default"}
        />
      ) : (
        "—"
      ),
  },
  {
    field: "mode",
    headerName: "Mode",
    width: 110,
    valueGetter: ({ value }) => {
      if (!value) return "—";
      return value === "ONLINE" ? "Online" : "In-person";
    },
  },
];

/**
 * The Patient's appointment history at THIS Facility — Local Data, loaded on
 * selection with no external call. Neither screen this module replaces showed it.
 */
export default function PastAppointmentsSection({ patientId }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!patientId) return undefined;
    const controller = new AbortController();

    setAppointments([]);
    setError("");
    setLoading(true);

    fetchPatientAppointments({ patientId, signal: controller.signal })
      .then((rows) => {
        if (controller.signal.aborted) return;
        // The grid refuses to render a row without an id, and appointment_id is
        // nullable in the response; the positional fallback is prefixed so it
        // can never collide with a real id.
        setAppointments(
          rows.map((row, index) => ({
            ...row,
            id: row.appointmentId || `idx-${index}`,
          })),
        );
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.error("Failed to load past appointments:", err);
        setError("Could not load this patient's appointment history.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [patientId]);

  return (
    <SectionDataGrid
      icon={<EventNoteOutlinedIcon />}
      title="Past Appointments"
      subtitle="Appointments this facility has booked for this patient."
      rows={appointments}
      columns={COLUMNS}
      loading={loading}
      error={error}
      loadingText="Loading appointment history…"
      emptyText="No appointments recorded for this patient yet."
    />
  );
}
